/**
 * ProjectInformationSection - "Project Information" form section molecule.
 *
 * Renders the framed section card that owns the job's descriptive
 * fields:
 *   - Project Name + Description (work_order only)
 *   - Business Line (shared by both form types)
 *   - Sub Type (work_order only)
 *
 * Business Line + Sub Type used to live in a per-line "Construction
 * Type" / "Janitorial Type" pair backed by hardcoded option arrays.
 * They are now driven by the CMS-managed business_lines tree fetched
 * from /api/business-lines — see businessLineService. The parent
 * supplies the loaded tree via the `businessLines` prop and reads back
 * the currently-selected `subType` slug from a single state slot.
 *
 * The Sub Type picker is opt-in via `showSubType` (inspections don't
 * use sub types) and, even when opted in, only renders once the
 * selected line has at least one active sub-type; lines without
 * sub-types skip it silently.
 */
import React from 'react';
import { SectionCard } from '../SectionCard';
import type { BusinessLine } from '../../../services/businessLineService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface ProjectInformationSectionProps {
	/** Whether to render the work-order-only Project Name + Description.
	 *  When false the section only renders the Business Line picker. */
	showProjectFields?: boolean;
	/** Whether to offer the Sub Type picker at all. Inspections have no
	 *  sub-type concept, so their form opts out entirely. */
	showSubType?: boolean;

	projectName?: string;
	onProjectNameChange?: (value: string) => void;
	description?: string;
	onDescriptionChange?: (value: string) => void;

	/** Currently-selected business line slug (empty string when none). */
	businessLine: string;
	onBusinessLineChange: (value: string) => void;
	/** Currently-selected sub type slug under the selected line.
	 *  Both are unused (and may be omitted) when showSubType is false. */
	subType?: string;
	onSubTypeChange?: (value: string) => void;
	/** CMS-managed line tree (filter to active=true at the call site). */
	businessLines: BusinessLine[];

	/** Inline validation errors keyed by field name. */
	errors?: Record<string, string>;
	/** Standard styling tokens. */
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	errorBorder: string;
	/** Optional extra classes for the outer wrapper. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const ProjectInformationSection: React.FC<ProjectInformationSectionProps> = ({
	showProjectFields = true,
	showSubType = true,
	projectName = '',
	onProjectNameChange,
	description = '',
	onDescriptionChange,
	businessLine,
	onBusinessLineChange,
	subType = '',
	onSubTypeChange,
	businessLines,
	errors,
	labelClass,
	inputClass,
	normalBorder,
	errorBorder,
	className = '',
}) => {
	/* Sub-types available under the currently-selected line. Empty
	   array when the caller opted out of sub types, when no line is
	   picked, OR when the line has no sub-types — the picker hides
	   itself in all three cases. */
	const selectedLine = businessLines.find((bl) => bl.slug === businessLine);
	const subTypeOptions = showSubType ? (selectedLine?.sub_types ?? []).filter((st) => st.is_active) : [];

	return (
		<SectionCard title="Project Information" className={`project-information-section ${className}`} dataRef="molecule-project-information-section">
			<div className="project-information-section__body space-y-16px">
				{/* Project Name + Description — work_order only. */}
				{showProjectFields && onProjectNameChange && (
					<div>
						<label htmlFor="projectName" className={labelClass}>Project Name <span className="text-red-500">*</span></label>
						<input id="projectName" type="text" value={projectName} onChange={(e) => onProjectNameChange(e.target.value)} className={`${inputClass} ${errors?.projectName ? errorBorder : normalBorder}`} placeholder="Enter project name" />
						{errors?.projectName && <p className="mt-4px text-14px text-red-600">{errors.projectName}</p>}
					</div>
				)}

				{showProjectFields && onDescriptionChange && (
					<div>
						<label htmlFor="description" className={labelClass}>Description <span className="text-red-500">*</span></label>
						<textarea id="description" value={description} onChange={(e) => onDescriptionChange(e.target.value)} rows={4} className={`block w-full rounded-md border px-12px py-10px text-16px font-sans focus:outline-none focus:ring-2 ${errors?.description ? errorBorder : normalBorder}`} placeholder="Describe the work to be performed..." />
						{errors?.description && <p className="mt-4px text-14px text-red-600">{errors.description}</p>}
					</div>
				)}

				{/* Business Line — CMS-managed. Required for work orders;
				    optional for inspections (validation lives in the parent). */}
				<div>
					<label htmlFor="businessLine" className={labelClass}>
						Business Line{showProjectFields && <span className="text-red-500"> *</span>}
					</label>
					<select
						id="businessLine"
						value={businessLine}
						onChange={(e) => {
							const next = e.target.value;
							onBusinessLineChange(next);
							/* Clear sub_type whenever the parent line
							   changes — a slug from the old line is never
							   valid under the new one. No-op for callers
							   that opted out of the picker. */
							onSubTypeChange?.('');
						}}
						className={`${inputClass} bg-white ${errors?.businessLine ? errorBorder : normalBorder}`}
					>
						<option value="">Select business line</option>
						{businessLines.filter((bl) => bl.is_active).map((bl) => (
							<option key={bl.id} value={bl.slug}>{bl.label}</option>
						))}
					</select>
					{errors?.businessLine && <p className="mt-4px text-14px text-red-600">{errors.businessLine}</p>}
				</div>

				{/* Sub Type — opt-in via showSubType, and then only
				    shown when the selected line has at least one active
				    sub-type. Label is line-agnostic ("Sub Type") so the
				    same UI works across whatever lines ops sets up in
				    the CMS. */}
				{showSubType && businessLine && subTypeOptions.length > 0 && (
					<div>
						<label htmlFor="subType" className={labelClass}>
							Sub Type
							{selectedLine && (
								<span className="ml-4px text-12px font-normal text-brand-text-muted">
									{`(${selectedLine.label})`}
								</span>
							)}
						</label>
						<select id="subType" value={subType} onChange={(e) => onSubTypeChange?.(e.target.value)} className={`${inputClass} bg-white ${normalBorder}`}>
							<option value="">Select sub type</option>
							{subTypeOptions.map((st) => (
								<option key={st.id} value={st.slug}>{st.label}</option>
							))}
						</select>
					</div>
				)}
			</div>
		</SectionCard>
	);
};

export default ProjectInformationSection;
